import { useEffect, useState } from "react";
import { api, ApiError } from "../api.ts";
import { Card } from "../ui.tsx";

export const isAdminPath = () => location.pathname.replace(/\/$/, "") === "/admin";

interface Summary {
  totalParents: number; totalChildren: number;
  signups: { today: number; last7: number; last30: number };
  billing: { free: number; active: number; trial: number; expired: number; noBillingRow: number };
  revenue: { currency: string; total: number; count: number }[];
  sources: { source: string; signups: number; tried: number; paying: number }[];
  campaigns: { source: string; campaign: string; signups: number }[];
  referrals: { invited: number; rewarded: number };
  usage: { totalSecs: number; activeFamilies7d: number };
}
interface ParentRow {
  id: string; email: string; name: string; createdAt: number; childCount: number;
  market: string | null; plan: string | null; paidUntil: number | null; trialEndsAt: number | null; status: string;
  phone: string | null; source: string | null; lastActive: number | null; totalSecs: number;
  trialEmails: { step: number; status: string; reason: string | null; dueAt: number; sentAt: number | null; opened: boolean; clicked: boolean }[];
  comebackEmail: { channel: string; status: string; reason: string | null; sentAt: number | null; opened: boolean; clicked: boolean; returned: boolean } | null;
}

const fmtDate = (ms: number) => new Date(ms).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const fmtMoney = (currency: string, smallestUnit: number) => {
  const amt = smallestUnit / 100;
  const symbol = currency === "INR" ? "₹" : currency === "AED" ? "AED " : currency + " ";
  return symbol + amt.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};
const fmtDur = (secs: number) => { if (!secs) return "–"; const m = Math.round(secs / 60); if (m < 1) return "<1 min"; if (m < 60) return m + " min"; const h = Math.floor(m / 60); return h + " h " + String(m % 60).padStart(2, "0") + " m"; };
const ago = (ms: number | null) => { if (!ms) return "never"; const m = Math.floor((Date.now() - ms) / 60e3); if (m < 2) return "just now"; if (m < 60) return m + " min ago"; const h = Math.floor(m / 60); if (h < 24) return h + " h ago"; const d = Math.floor(h / 24); return d < 30 ? d + (d === 1 ? " day ago" : " days ago") : fmtDate(ms); };
/** One small coloured chip per email, so you can see at a glance how far a family has got. */
const STOP_WHY: Record<string, string> = { subscribed: "subscribed", optout: "unsubscribed", free: "team account", trial_extended: "trial extended", stale: "missed", interrupted: "interrupted" };
function stage(n: ParentRow["trialEmails"][number]) {
  if (n.status === "sent") return n.clicked ? { cls: "c", txt: "clicked" } : n.opened ? { cls: "o", txt: "opened" } : { cls: "s", txt: "sent" };
  if (n.status === "scheduled" || n.status === "sending") return { cls: "w", txt: "due " + fmtDate(n.dueAt) };
  if (n.status === "cancelled") return { cls: "x", txt: "stopped: " + (STOP_WHY[n.reason || ""] || n.reason || "") };
  if (n.status === "skipped") return { cls: "x", txt: "skipped" };
  return { cls: "f", txt: "failed" };
}
function TrialFlow({ p }: { p: ParentRow }) {
  if (!p.trialEmails.length) return <span className="note">{p.status === "trial" ? "Trial running" : p.status === "active" ? "Paid" : p.status === "expired" ? "Not started" : "—"}</span>;
  return <span className="chips">{[1, 2, 3].map((s) => { const n = p.trialEmails.find((x) => x.step === s); if (!n) return <span key={s} className="chip none" title={`Email ${s}: not planned`}>{s}</span>; const g = stage(n); return <span key={s} className={`chip ${g.cls}`} title={`Email ${s}: ${g.txt}`}>{s} {g.txt}</span>; })}</span>;
}
function BackFlow({ p }: { p: ParentRow }) {
  const k = p.comebackEmail; if (!k) return <span className="note">—</span>;
  const g = k.status === "failed" ? { cls: "f", txt: "failed" } : k.clicked ? { cls: "c", txt: "clicked" } : k.opened ? { cls: "o", txt: "opened" } : { cls: "s", txt: "sent" };
  return <span className="chips"><span className={`chip ${g.cls}`} title={`${k.channel} reminder: ${g.txt}`}>{k.channel === "whatsapp" ? "WhatsApp" : "Email"} {g.txt}</span>{k.returned && <span className="chip c" title="Used Numi after the reminder">came back</span>}</span>;
}
const STATUS_LABEL: Record<string, string> = { free: "Free (team)", active: "Paid", trial: "Trial", expired: "Expired", no_billing_row: "No billing row" };

function LoginGate({ onIn }: { onIn: () => void }) {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true); setErr("");
    try { await api("/auth/login", { body: { email: email.trim().toLowerCase(), password } }); onIn(); }
    catch (e: any) { setErr(e instanceof ApiError ? e.message : "Something went wrong."); }
    setBusy(false);
  };
  return <main style={{ maxWidth: 380, margin: "80px auto" }}>
    <Card>
      <h1 style={{ marginTop: 0 }}>Numi admin</h1>
      <p className="note">Sign in with an admin-listed parent account.</p>
      <div className="fields">
        <div className="field"><label className="lbl" htmlFor="aEmail">Email</label><input id="aEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} /></div>
        <div className="field"><label className="lbl" htmlFor="aPass">Password</label><input id="aPass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} /></div>
      </div>
      {err && <p className="note" style={{ color: "#c0392b" }}>{err}</p>}
      <button className="btn yellow" disabled={busy || !email || !password} onClick={submit}>Sign in</button>
    </Card>
  </main>;
}

function StatTile({ label, value }: { label: string; value: string | number }) {
  return <div className="admin-tile"><b>{value}</b><span>{label}</span></div>;
}

const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) + "%" : "–");
type Mode = "off" | "dry" | "live";
interface NudgeData { mode: Mode; emailReady: boolean; steps: { step: number; sent: number; opened: number; clicked: number; subscribed: number }[] }
interface ReportData { mode: Mode; emailReady: boolean; optOuts: number; sent: number; failed: number; opened: number; clicked: number; quiet: number; lastWeek: { week: string; sent: number } | null }
interface ComebackData { mode: Mode; emailReady: boolean; whatsappReady: boolean; whatsappOptIns: number; channels: { channel: string; sent: number; opened: number; clicked: number; returned: number }[] }
const sum = <T,>(xs: T[], f: (x: T) => number) => xs.reduce((a, x) => a + (f(x) || 0), 0);

function ReminderCard({ title, what, mode, sentence, tests, extra, busy, onTest, onMode }: {
  title: string; what: string; mode: Mode; sentence: string; tests: { label: string; key: string }[]; extra?: string; busy: string;
  onTest: (key: string) => void; onMode: (m: Mode) => void;
}) {
  const live = mode === "live";
  return <div className="rcard">
    <h3 style={{ margin: 0 }}>{title}</h3>
    <p className="note" style={{ margin: "4px 0 10px" }}>{what}</p>
    <div className="row" style={{ alignItems: "center", flexWrap: "wrap" }}>
      <span className={`rpill ${live ? "on" : "test"}`}>{live ? "ON: emails are going to parents" : "TEST MODE: nothing is sent to parents"}</span>
      {live ? <button className="btn soft sm" disabled={!!busy} onClick={() => onMode("dry")}>Pause</button>
        : <button className="btn yellow sm" disabled={!!busy} onClick={() => { if (window.confirm("Turn this ON? Real emails will start going to parents.")) onMode("live"); }}>Turn ON</button>}
    </div>
    <p style={{ margin: "10px 0 6px" }}><b>{sentence}</b></p>
    {extra && <p className="note" style={{ margin: "0 0 8px" }}>{extra}</p>}
    <div className="row" style={{ flexWrap: "wrap" }}>
      {tests.map((t) => <button key={t.key} className="btn soft sm" disabled={!!busy} onClick={() => onTest(t.key)}>{busy === t.key ? "Sending..." : t.label}</button>)}
    </div>
  </div>;
}

/** One simple place for the two kinds of reminder emails. */
function RemindersPanel() {
  const [n, setN] = useState<NudgeData | null>(null); const [c, setC] = useState<ComebackData | null>(null); const [w, setW] = useState<ReportData | null>(null); const [pv, setPv] = useState<{ subject: string; html: string; to: string } | null>(null);
  const [to, setTo] = useState(""); const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null); const [busy, setBusy] = useState("");
  const load = () => { api<NudgeData>("/admin/nudges").then(setN).catch(() => {}); api<ComebackData>("/admin/comebacks").then(setC).catch(() => {}); api<ReportData>("/admin/reports").then(setW).catch(() => {}); };
  useEffect(() => { load(); }, []);
  if (!n || !c || !w) return null;
  const test = async (key: string) => {
    setBusy(key); setMsg(null);
    try {
      if (key === "wp") { const r = await api<any>(`/admin/report-preview${to.trim() ? `?email=${encodeURIComponent(to.trim())}` : ""}`); setPv(r); setMsg({ ok: true, text: `Showing this week's report for ${r.to} below. Nothing was sent.` }); setBusy(""); return; }
      const r = key === "c" ? await api("/admin/comeback-test", { body: { email: to.trim() || undefined } }) : key === "w" ? await api("/admin/report-test", { body: { email: to.trim() || undefined } }) : await api("/admin/nudge-test", { body: { step: Number(key), email: to.trim() || undefined } }); setMsg({ ok: true, text: `Test email sent to ${r.to}. Check that inbox in a minute.` }); }
    catch (e: any) { setMsg({ ok: false, text: e.message }); }
    setBusy("");
  };
  const mode = async (which: "nudge" | "comeback" | "report", m: Mode) => {
    setBusy("m"); setMsg(null);
    try { await api("/admin/mode", { body: { which, mode: m } }); setMsg({ ok: true, text: m === "live" ? "Turned ON." : "Paused. Nothing will be sent." }); load(); }
    catch (e: any) { setMsg({ ok: false, text: e.message }); }
    setBusy("");
  };
  const ns = sum(n.steps, (x) => x.sent), no = sum(n.steps, (x) => x.opened), nc = sum(n.steps, (x) => x.clicked), nsub = sum(n.steps, (x) => x.subscribed);
  const cs = sum(c.channels, (x) => x.sent), co = sum(c.channels, (x) => x.opened), cb = sum(c.channels, (x) => x.returned);
  return <section>
    <h2 style={{ marginTop: 32 }}>Reminder emails</h2>
    <div className="field" style={{ maxWidth: 460 }}>
      <label className="lbl" htmlFor="rTo">Send tests to this email</label>
      <input id="rTo" className="inp" type="email" placeholder="Leave empty to send to yourself" value={to} onChange={(e) => setTo(e.target.value)} />
      <small className="note">Want to see how it looks for a real family? Type their account email. The test shows their child's name and goes only to that address.</small>
    </div>
    {msg && <p className="note" role="status" style={{ color: msg.ok ? "#1d7a3a" : "#c0392b", fontWeight: 700 }}>{msg.text}</p>}
    <div className="rgrid">
      <ReminderCard title="1. Free trial ended" what="When a family's free week ends, Numi emails the parent up to 3 times (every 2 days) to invite them to subscribe. It stops as soon as they subscribe or unsubscribe."
        mode={n.mode} busy={busy} onMode={(m) => mode("nudge", m)} onTest={test}
        tests={[{ label: "Send me test email 1", key: "1" }, { label: "Test email 2", key: "2" }, { label: "Test email 3", key: "3" }]}
        sentence={ns ? `So far: ${ns} sent, ${no} opened, ${nc} clicked, ${nsub} subscribed afterwards.` : "Nothing sent yet."} />
      <ReminderCard title="2. Gone quiet" what="If a child hasn't used Numi for 2 days, Numi sends one friendly, fun reminder (at most one a week). It stops when they come back."
        mode={c.mode} busy={busy} onMode={(m) => mode("comeback", m)} onTest={test} tests={[{ label: "Send me a test", key: "c" }]}
        extra={c.whatsappReady ? `Goes on WhatsApp to parents who said yes (${c.whatsappOptIns} so far), otherwise by email.` : `Goes by email. (WhatsApp isn't connected yet; ${c.whatsappOptIns} parent${c.whatsappOptIns === 1 ? " has" : "s have"} said yes to it.)`}
        sentence={cs ? `So far: ${cs} sent, ${co} opened, ${cb} came back to play.` : "Nothing sent yet."} />
      <ReminderCard title="3. Weekly report" what="Every Sunday evening (5–8 pm, family's time), parents get one warm email: the week's highs, where Numi is helping next, a question to try at dinner. Only families who used Numi in the last 2 weeks; quiet weeks get a short version."
        mode={w.mode} busy={busy} onMode={(m) => mode("report", m)} onTest={test} tests={[{ label: "Preview here", key: "wp" }, { label: "Send me a test", key: "w" }]}
        extra={`${w.optOuts} parent${w.optOuts === 1 ? " has" : "s have"} turned the weekly report off.`}
        sentence={w.sent ? `So far: ${w.sent} sent, ${w.opened} opened, ${w.clicked} clicked${w.lastWeek ? ` (last Sunday: ${w.lastWeek.sent})` : ""}.` : "Nothing sent yet."} />
    </div>
    {pv && <div className="rpreview"><div className="row between"><b>{pv.subject}</b><button className="btn soft sm" onClick={() => setPv(null)}>Close preview</button></div>
      <iframe title="Weekly report preview" srcDoc={pv.html} sandbox="" style={{ width: "100%", height: 900, border: "1px solid var(--line)", borderRadius: 12, background: "#fff" }} /></div>}
    {!n.emailReady && <p className="note" style={{ color: "#c0392b" }}>Email isn't connected yet, so these can't be turned ON.</p>}
  </section>;
}

export function Admin() {
  const [state, setState] = useState<"checking" | "out" | "forbidden" | "in">("checking");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [parents, setParents] = useState<ParentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState(""); const [offset, setOffset] = useState(0); const [sort, setSort] = useState<"created" | "active" | "time">("created");
  const limit = 50;

  const load = async () => {
    try {
      const [sum, list] = await Promise.all([
        api<Summary>("/admin/summary"),
        api<{ parents: ParentRow[]; total: number }>(`/admin/parents?limit=${limit}&offset=${offset}&q=${encodeURIComponent(q)}&sort=${sort}`),
      ]);
      setSummary(sum); setParents(list.parents); setTotal(list.total); setState("in");
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 401) setState("out");
      else if (e instanceof ApiError && e.status === 403) setState("forbidden");
      else setState("out");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [offset, sort]);
  useEffect(() => { const t = setTimeout(() => { setOffset(0); load(); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [q]);

  if (state === "checking") return <main style={{ padding: 40 }}><p>Loading…</p></main>;
  if (state === "out") return <LoginGate onIn={() => { setState("checking"); load(); }} />;
  if (state === "forbidden") return <main style={{ maxWidth: 420, margin: "80px auto" }}><Card><h1 style={{ marginTop: 0 }}>Not an admin account</h1><p className="note">That account signed in fine, but isn't on the admin allowlist. Ask whoever deployed Numi to add this email to ADMIN_EMAILS.</p></Card></main>;
  if (!summary) return null;

  return <main style={{ maxWidth: 1360, margin: "0 auto", padding: "24px 16px 60px" }}>
    <h1>Numi admin</h1>
    <div className="admin-tiles">
      <StatTile label="Total signups" value={summary.totalParents} />
      <StatTile label="Children added" value={summary.totalChildren} />
      <StatTile label="New today" value={summary.signups.today} />
      <StatTile label="New last 7 days" value={summary.signups.last7} />
      <StatTile label="New last 30 days" value={summary.signups.last30} />
      <StatTile label="Paying now" value={summary.billing.active} />
      <StatTile label="On free trial" value={summary.billing.trial} />
      <StatTile label="Trial expired" value={summary.billing.expired} />
    </div>
    {summary.revenue.length > 0 && (
      <p className="note">Revenue collected (all time): {summary.revenue.map((r) => `${fmtMoney(r.currency, r.total)} (${r.count} payments)`).join(" · ")}</p>
    )}

    <p className="note">Total time spent in the app (since tracking began): <b>{fmtDur(summary.usage.totalSecs)}</b> in total · <b>{summary.usage.activeFamilies7d}</b> {summary.usage.activeFamilies7d === 1 ? "family" : "families"} active in the last 7 days · Referrals: <b>{summary.referrals.invited}</b> joined by invite, <b>{summary.referrals.rewarded}</b> rewarded.</p>

    <h2 style={{ marginTop: 32 }}>Where signups come from</h2>
    <p className="note">"Tried" = answered at least one question. "Paying" = has paid. Links: add <code>?src=whatsapp</code> (or <code>&amp;campaign=school-groups</code>) to any Numi link you share.</p>
    <div style={{ overflowX: "auto" }}><table className="admin-table"><thead><tr><th>Source</th><th>Signups</th><th>Tried Numi</th><th>Paying</th></tr></thead><tbody>
      {summary.sources.map((r) => <tr key={r.source}><td>{r.source}</td><td>{r.signups}</td><td>{r.tried} ({pct(r.tried, r.signups)})</td><td>{r.paying} ({pct(r.paying, r.signups)})</td></tr>)}
    </tbody></table></div>
    {summary.campaigns.length > 0 && <details><summary className="note">Campaigns</summary><table className="admin-table"><thead><tr><th>Source</th><th>Campaign</th><th>Signups</th></tr></thead><tbody>
      {summary.campaigns.map((r, i) => <tr key={i}><td>{r.source}</td><td>{r.campaign}</td><td>{r.signups}</td></tr>)}</tbody></table></details>}

    <RemindersPanel />

    <h2 style={{ marginTop: 32 }}>Signups</h2>
    <div className="row" style={{ marginBottom: 12 }}>
      <input className="inp" placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 320 }} />
      <label className="note" htmlFor="aSort">Sort by</label>
      <select id="aSort" value={sort} onChange={(e) => { setOffset(0); setSort(e.target.value as any); }}><option value="created">Newest signup</option><option value="active">Last used</option><option value="time">Most total time spent</option></select>
    </div>
    <p className="note">Email columns: each small tag is one email. <span className="chip s">sent</span> <span className="chip o">opened</span> <span className="chip c">clicked</span> <span className="chip w">due (waiting)</span> <span className="chip x">stopped</span> <span className="chip f">failed</span> <span className="chip none">not planned</span></p>
    <div style={{ overflowX: "auto" }}>
      <table className="admin-table">
        <thead><tr><th>Signed up</th><th>Name</th><th>Email</th><th>Mobile</th><th>Kids</th><th>Market</th><th>Status</th><th>Plan</th><th>Source</th><th>Last used</th><th>Total time spent</th><th>Trial-ended emails</th><th>Gone-quiet email</th></tr></thead>
        <tbody>
          {parents.map((p) => <tr key={p.id}>
            <td>{fmtDate(p.createdAt)}</td>
            <td>{p.name || "—"}</td>
            <td>{p.email}</td>
            <td>{p.phone || "—"}</td>
            <td>{p.childCount}</td>
            <td>{p.market || "—"}</td>
            <td>{STATUS_LABEL[p.status] || p.status}</td>
            <td>{p.plan ? (p.plan === "m3" ? "3 months" : "1 month") : "—"}</td>
            <td>{p.source || "—"}</td>
            <td title={p.lastActive ? new Date(p.lastActive).toLocaleString() : ""}>{ago(p.lastActive)}</td>
            <td>{fmtDur(p.totalSecs)}</td>
            <td><TrialFlow p={p} /></td>
            <td><BackFlow p={p} /></td>
          </tr>)}
        </tbody>
      </table>
    </div>
    <div className="row" style={{ marginTop: 12, justifyContent: "space-between" }}>
      <span className="note">{total} total{q ? ` matching "${q}"` : ""}</span>
      <span className="row">
        <button className="btn soft sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Previous</button>
        <button className="btn soft sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Next</button>
      </span>
    </div>
  </main>;
}
